import { isFeatureEnabled } from '@iheartjane/config';
import { JANE_DEFINED_ROWS } from '@iheartjane/menu';
import type { ButtonProps } from '@iheartjane/reefer';
import { Button, Flex, Modal, useToast } from '@iheartjane/reefer';
import { Form, useFormContext } from '@iheartjane/reefer-hook-form';
import { useEffect, useMemo, useState } from 'react';

import { ConfirmDisableModal } from '@jane/business-admin/components/ConfirmDisableModal';
import { SwitchWithBorder } from '@jane/business-admin/components/SwitchWithBorder';
import { useDeleteMenuRow } from '@jane/business-admin/data-access';
import { useCatchErrorsWithManager } from '@jane/business-admin/hooks';
import type { MenuRow } from '@jane/business-admin/types';
import { EventNames, ModalNames, track } from '@jane/business-admin/util';
import { ConfirmDeleteModal } from '@jane/shared-b2b/components';
import { rootOnlyCustomRowsAllowed } from '@jane/shared/util';

import type { FormValues } from './form.types';
import { FORM_ERROR_NAME } from './form.types';

interface DeleteButtonProps extends ButtonProps {
  onDelete: () => void;
}
const DeleteButtonWithConfirmation = ({
  onDelete,
  ...buttonProps
}: DeleteButtonProps) => {
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
  return (
    <>
      <Button
        {...buttonProps}
        variant="destructive"
        label="Delete"
        onClick={() => setConfirmDeleteModalOpen(true)}
      />
      {confirmDeleteModalOpen && (
        <ConfirmDeleteModal
          open={confirmDeleteModalOpen}
          setOpen={setConfirmDeleteModalOpen}
          confirmDelete={onDelete}
        />
      )}
    </>
  );
};

interface Props {
  closeModal: () => void;
  hasEmbeddedMenuRow?: boolean;
  isCreateMode: boolean;
  row?: MenuRow;
  storeId: string;
}
export const ModalHeader = ({
  closeModal,
  hasEmbeddedMenuRow,
  isCreateMode,
  row,
  storeId,
}: Props) => {
  const { mutateAsync: deleteMenuRow, isSuccess: deleteSuccess } =
    useDeleteMenuRow({ rowId: row?.id || 0, storeId });
  const toast = useToast();
  const {
    setError,
    setValue,
    formState: { isDirty },
  } = useFormContext<FormValues>();
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const catchSubmitErrors = useCatchErrorsWithManager(
    `Error deleting menu row. Please try again.`
  );

  const showRootsOnlyToggle =
    isFeatureEnabled('rootsOnlyCustomRows') &&
    rootOnlyCustomRowsAllowed(storeId);

  const showDeleteButton = useMemo(() => {
    if (isCreateMode) return false;
    return !JANE_DEFINED_ROWS.includes(row?.menu_row_type || '');
  }, [row, isCreateMode]);

  useEffect(() => {
    if (deleteSuccess) {
      toast.add({
        label: `Menu row deleted!`,
        variant: 'success',
      });
      closeModal();
    }
  }, [deleteSuccess]);

  const handleDelete = () => {
    track({
      event: EventNames.DeleteStoreEntity,
      modal_name: ModalNames.EditMenuRow,
      entity_name: 'menu_row',
    });
    return catchSubmitErrors({
      submitMethod: () => deleteMenuRow(),
      requestData: {},
      onValidationError: () => null,
      callback: () => {
        setError(FORM_ERROR_NAME, {
          message: 'Error deleting menu row. Please try again.',
        });
      },
    });
  };

  return (
    <Modal.Header
      title={`${isCreateMode ? 'Add' : 'Edit'} Menu Row`}
      subtitle={`${row?.id || ''}`}
      actions={
        <Flex alignItems="center" gap={16}>
          {showRootsOnlyToggle && (
            <SwitchWithBorder name="roots_only" label="Visible in Roots API" />
          )}
          <SwitchWithBorder
            name="enabled"
            label="Enabled"
            onChange={(visible: boolean) => {
              if (hasEmbeddedMenuRow && !visible) {
                setValue('enabled', true, { shouldDirty: false });
                setOpenConfirmModal(true);
                return;
              }

              const { enabled = false } = row || {};

              if (!isDirty && visible === enabled) return;

              track({
                event: EventNames.ToggleVisibility,
                final_state: visible ? 'visible' : 'hidden',
                object: 'menu row',
                successful: true,
                trigger_source_id: 'row workspace',
              });
            }}
          />
          <ConfirmDisableModal
            itemName={row?.custom_display_name || 'this row'}
            type="embedded"
            onConfirm={() => {
              setOpenConfirmModal(false);
              setValue('enabled', false, { shouldDirty: true });
              track({
                event: EventNames.ToggleVisibility,
                final_state: 'hidden',
                object: 'menu row',
                successful: true,
                trigger_source_id: 'row workspace',
              });
            }}
            onCancel={() => {
              setOpenConfirmModal(false);
              setValue('enabled', true, { shouldDirty: false });
            }}
            open={openConfirmModal}
          />
          {showDeleteButton && (
            <DeleteButtonWithConfirmation onDelete={handleDelete} />
          )}
          <Form.SubmitButton variant="primary" label="Publish" />
        </Flex>
      }
    />
  );
};
